import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CacheService } from 'src/app/core/services/cache/cache.service';
import { FacadeService } from 'src/app/core/services/facade.service';
import { LogService } from 'src/app/core/services/log/log.service';
import { Employeur } from 'src/app/shared/models/employeur';
import { NominatimResponse } from 'src/app/shared/models/api-response/nominatim-response';
import {
	PARAM_LABEL_BENEFICIAIRE,
	PARAM_LABEL_INTERVENANT
} from 'src/app/core/services/constant/constants.service';
import {
	AbstractControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
} from '@angular/forms';
import { Address } from 'src/app/shared/models/address';
import { Observable, of, Subscriber } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { TypeStructure } from 'src/app/shared/models/typeStructure';
import { TypeEntreprise } from 'src/app/shared/models/typeEntreprise';

@Component({
	selector: 'app-employeur-form',
	templateUrl: './employeur-form.component.html',
	styleUrls: ['./employeur-form.component.scss']
})
export class EmployeurFormComponent implements OnInit {
	/**
	 * Employeur
	 */
	@Input() employeur: Employeur;

	/**
	 * Vrai si formulaire d'ajout, faux sinon (formulaire de détail)
	 */
	@Input() isAddForm: boolean;

	/**
	 * Addresse sélectionner dans le typeahead
	 */
	@Input() isAddressSelected: { value: boolean };

	/**
	 * Booleen indiquant si il s'agit de l'addresse par défaut ou non
	 */
	@Input() isAddressDefault: { value: boolean };

	/**
	 * Formulaire angular
	 */
	@Input() form: FormGroup;

	/**
	 * Boolean empêchant l'utilisateur d'envoyer plusieurs fois le formulaire
	 */
	@Input() isValidateButtonClicked: { value: boolean };

	/**
	 * Signal émis lors de l'envoi du formulaire pour que la sauvegarde du formulaire ce fasse dans le composant parent
	 */
	@Output() sendForm: EventEmitter<any> = new EventEmitter();

	/**
	 * Signal émis pour que la modal soit fermée
	 */
	@Output() hideModal: EventEmitter<any> = new EventEmitter();

	LABEL_BENEFICIAIRE: string;
	LABEL_INTERVENANT: string;
	MAX_NAME_CHAR = 25;

	/**
	 * Addresse selectionner
	 */
	selectedaddress?: Address;

	/**
	 * Typeahead des adresse en chargement
	 */
	typeaheadLoading?: boolean;

	/**
	 * Liste des adresse disponibles dans le typeahead
	 */
	addressList: Observable<NominatimResponse[]>;

	/**
	 * Liste des adresse récupérer grace au backend
	 */
	searchResults: NominatimResponse[];
	listTypeStructure: TypeEntreprise[];

	isPreferencesActive = false;

	constructor(
		private facade: FacadeService,
		private logger: LogService,
	) {
		this.addressList = new Observable((observer: Subscriber<string>) => {
			// Runs on every search
			observer.next(this.form.value.addressStructure);
		}).pipe(
			mergeMap((token: string) =>
				this.getNominatimDisplaynameAsObservable(token)
			)
		);
	}

	ngOnInit(): void {
		if (this.employeur) {
			this.facade.typeEntrepriseService.getAllTypeEntreprise().subscribe({
				next: (val) => {
					this.listTypeStructure = val;
				},
				error: (err) => {
					this.logger.error(err);
				}
			});
		}

		this.getAllEmployeesAndSetEmployeesInForm();
	}

	getAllEmployeesAndSetEmployeesInForm() {
		this.facade.employeeService.getAllActiveEmployees().subscribe((data) => {
			if (!this.isAddForm) {
				if (this.employeur.addressStructure && this.employeur.addressStructure.label) {
					this.isAddressDefault.value = true;
				}
				/**
				 * Récupère les données et initialise le formulaire
				 */
				this.form.patchValue({
					addressStructure: this.employeur.addressStructure.label || '',
					nomStructure: this.employeur.nomStructure,
					typeEntreprise: this.employeur.typeEntreprise?.id,
					siret: this.employeur.siret,
					codeNaf: this.employeur.codeNaf,
					lastNameContact1: this.employeur.lastNameContact1,
					firstNameContact1: this.employeur.firstNameContact1,
					fonctionContact1: this.employeur.fonctionContact1,
					telephoneContact1: this.employeur.telephoneContact1,
					emailContact1: this.employeur.emailContact1,
					lastNameContact2: this.employeur.lastNameContact2,
					firstNameContact2: this.employeur.firstNameContact2,
					fonctionContact2: this.employeur.fonctionContact2,
					telephoneContact2: this.employeur.telephoneContact2,
					emailContact2: this.employeur.emailContact2,
				});
			}
		});
	}

	/**
	 * Méthode appeler lors de la sélection d'un élément dans le typeahead
	 * @param event
	 */
	onAddressSelect(event: TypeaheadMatch) {
		this.isAddressDefault.value = false;
		this.isAddressSelected.value = true;
		this.selectedaddress = new Address();
		this.selectedaddress.label = event.item.displayName;
		this.selectedaddress.latitude = event.item.latitude;
		this.selectedaddress.longitude = event.item.longitude;
		this.form.controls['addressStructure'].updateValueAndValidity();
	}

	getNominatimDisplaynameAsObservable(
		token: string
	): Observable<NominatimResponse[]> {
		this.isAddressSelected.value = false;
		this.facade.nominatimService.addressLookup(token).subscribe({
			next: (result: any) => {
				this.searchResults = result.features.map(
					(feature) =>
						new NominatimResponse(
							feature.geometry.coordinates[0],
							feature.geometry.coordinates[1],
							feature.properties.label
						)
				);
			},
			error: (err) => {
				this.logger.log(
					'' + err.status + ' ' + err.error.message ? err.error.message : ''
				);
			}
		});
		return of(this.searchResults);
	}

	changeTypeaheadLoading(e: boolean): void {
		this.typeaheadLoading = e;
	}

	/**
	 * Fonction appelée si une adresse commence à être tapée.
	 */
	selectedAddressKeyUp(e: any) {
		this.isAddressDefault.value = false;
		if (this.selectedaddress && this.selectedaddress.latitude != 0) {
			if (this.selectedaddress?.label === e.value) {
				this.isAddressSelected.value = true;
			}
		}else{
			this.selectedaddress = new Address();
			this.selectedaddress.label = e.value
			this.isAddressSelected.value = true;
		}
		this.form.controls['addressStructure'].updateValueAndValidity();
	}

	/**
	 * Méthode d'envoie de formulaire, si celui est valide stock les données et envoie un signal au composant parent
	 */
	submitForm() {
		if (this.form.valid) {
			this.employeur.nomStructure = this.form.value.nomStructure;
			if (this.isAddressSelected.value && this.selectedaddress !== null) {
				this.employeur.addressStructure = this.selectedaddress;
			} else {
				if (!this.isAddressDefault.value) {
					this.employeur.addressStructure.label = this.form.value.address;
				}
			}
			this.employeur.typeEntreprise = this.listTypeStructure.find(x => x.id == this.form.get('typeEntreprise').value); 
			this.employeur.siret = this.form.value.siret;
			this.employeur.codeNaf = this.form.value.codeNaf;
			this.employeur.lastNameContact1 = this.form.value.lastNameContact1;
			this.employeur.firstNameContact1 = this.form.value.firstNameContact1;
			this.employeur.fonctionContact1 = this.form.value.fonctionContact1;
			this.employeur.telephoneContact1 = this.form.value.telephoneContact1;
			this.employeur.emailContact1 = this.form.value.emailContact1;
			this.employeur.lastNameContact2 = this.form.value.lastNameContact2;
			this.employeur.firstNameContact2 = this.form.value.firstNameContact2;
			this.employeur.fonctionContact2 = this.form.value.fonctionContact2;
			this.employeur.telephoneContact2 = this.form.value.telephoneContact2;
			this.employeur.emailContact2 = this.form.value.emailContact2;

			this.sendForm.emit();
		}
	}

	get nomStructure() {
		return this.form.get('nomStructure');
	}
	get addressStructure() {
		return this.form?.get('addressStructure');
	}
	get typeEntreprise() {
		return this.form?.get('typeEntreprise');
	}
	get siret() {
		return this.form?.get('siret');
	}
	get codeNaf() {
		return this.form?.get('codeNaf');
	}
	get lastNameContact1() {
		return this.form.get('lastNameContact1');
	}
	get firstNameContact1() {
		return this.form?.get('firstNameContact1');
	}
	get fonctionContact1() {
		return this.form?.get('fonctionContact1');
	}
	get telephoneContact1() {
		return this.form?.get('telephoneContact1');
	}
	get emailContact1() {
		return this.form?.get('emailContact1');
	}
	get lastNameContact2() {
		return this.form.get('lastNameContact2');
	}
	get firstNameContact2() {
		return this.form?.get('firstNameContact2');
	}
	get fonctionContact2() {
		return this.form?.get('fonctionContact2');
	}
	get telephoneContact2() {
		return this.form?.get('telephoneContact2');
	}
	get emailContact2() {
		return this.form?.get('emailContact2');
	}


	isAddressValidValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			return !(this.isAddressDefault.value || this.isAddressSelected.value)
				? { invalidAddress: { value: control.value } }
				: null;
		};
	}

}
